import { SensorConfiguration, SensorParameters, SensorParameterValidator } from "./SensorConfigurationTypes.js";

/**
 * Combines multiple validator functions into one validator function to make validation more modular.
 * Each validator is executed in sequence until one fails or all pass.
 *
 * Example:
 *
 * ```typescript
 * {
 *     validator: combineValidators(
 *         sensorParameterNumericValidators.isNumber,
 *         sensorParameterNumericValidators.isGreaterThanZero,
 *     )
 * }
 * ```
 *
 * The above will validate that the input is a number and greater than zero, and provide error messages
 * if either condition fails.
 *
 * @param validators - Array of validator functions to combine
 * @returns A single validator function that returns [true, ""] if all validators pass,
 *          or [false, errorMessage] from the first validator that fails
 */
export function combineValidators<ValueType>(
    ...validators: SensorParameterValidator<ValueType>[]
): SensorParameterValidator<ValueType> {
    return (value) => {
        for (let i = 0; i < validators.length; i++) {
            const result = validators[i](value);
            if (!result[0]) {
                return result;
            }
        }
        return [true, ""];
    };
}

/**
 * Standard numeric parameter validators to reduce code duplication.
 * Each validator returns [true, ""] if valid, or [false, errorMessage] if invalid.
 */
export const sensorParameterNumericValidators = {
    /**
     * Validates that a value is greater than or equal to zero
     */
    isGreaterThanOrEqualToZero: (value) =>
        value < 0 ? [false, "value must be greater than or equal to 0"] : [true, ""],

    /**
     * Validates that a value is strictly greater than zero
     */
    isGreaterThanZero: (value) => (value <= 0 ? [false, "value must be greater than 0"] : [true, ""]),

    /**
     * Validates that a value is less than or equal to zero
     */
    isLessThanOrEqualToZero: (value) => (value > 0 ? [false, "value must be less than or equal to 0"] : [true, ""]),
    /**
     * Validates that a value is strictly less than zero
     */
    isLessThanZero: (value) => (value >= 0 ? [false, "value must be less than 0"] : [true, ""]),

    /**
     * Validates that a value is a valid number (not NaN)
     */
    isNumber: (value) => {
        if (typeof value === "number" && !Number.isNaN(value)) {
            return [true, ""];
        }
        return [false, "value must be a number"];
    },
} as const satisfies Record<string, SensorParameterValidator<number>>;

/**
 * Standard string parameter validators to reduce code duplication.
 * Each validator returns [true, ""] if valid, or [false, errorMessage] if invalid.
 */
export const sensorParameterStringValidators = {
    /**
     * Validates that a value is a string
     */
    isString: (value) => {
        // The instanceof String check handles String objects created with new String()
        // @ts-ignore
        if (typeof value === "string" || value instanceof String) {
            return [true, ""];
        }
        return [false, "value must be a string"];
    },
} as const satisfies Record<string, SensorParameterValidator<string>>;

/**
 * Runs validation across all sensor parameters. If the verification passes, it return [true, ""].
 * If the verification fails it returns [false, "reason string"] where the reason string identifies all the problems with
 * the sensor parameters.
 * @param sensorParameters
 * @param sensorConfig
 * @returns {[boolean, string]}
 */
export function validateAllSensorParameters(
    sensorParameters: SensorParameters,
    sensorConfig: SensorConfiguration,
): [boolean, string] {
    const allProblems = [];

    Object.values(sensorConfig.sensorParameterConfigs).forEach((parameterConfig) => {
        const paramValue = sensorParameters[parameterConfig.key];
        const validationResult = parameterConfig.validator(paramValue);
        if (!validationResult[0]) {
            allProblems.push(
                `Invalid sensor parameter: ${parameterConfig.key}=${paramValue} for sensor type ${sensorConfig.key}. ` +
                    `Validation failed with reason: ${validationResult[1] || "Unknown validation error"}`,
            );
        }
    });

    // Check for unknown parameters that aren't in the configuration
    Object.keys(sensorParameters).forEach((parameterKey) => {
        if (!sensorConfig.sensorParameterConfigs[parameterKey]) {
            allProblems.push(
                `Unknown sensor parameter: ${parameterKey} for sensor type ${sensorConfig.key}. ` +
                    `This parameter is not defined in the sensor configuration.`,
            );
        }
    });

    if (allProblems.length === 0) {
        return [true, ""];
    } else if (allProblems.length === 1) {
        return [false, allProblems[0]];
    } else {
        return [false, `Multiple problems with sensor parameters: \n\n` + allProblems.join(`\n\n`)];
    }
}
